import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Translate as TranslateIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { HtmlContent, HtmlContentFormData } from '../../types';
import toast from 'react-hot-toast';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface Language {
  _id: string;
  name: string;
  code: string;
  nativeName: string;
  isActive: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`content-tabpanel-${index}`}
      aria-labelledby={`content-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function to get display title (handles both string and multilingual formats)
const getDisplayTitle = (title: string | Record<string, string> | undefined): string => {
  if (!title) return '';
  if (typeof title === 'string') return title;
  // Multilingual format - prefer EN, fallback to first available
  return title.EN || title.en || Object.values(title)[0] || '';
};

// Helper function to get display content
const getDisplayContent = (content: string | Record<string, string> | undefined): string => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  // Multilingual format - prefer EN, fallback to first available
  return content.EN || content.en || Object.values(content)[0] || '';
};

// Rich Text Editor Component using Tiptap
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const [pasteHtmlDialogOpen, setPasteHtmlDialogOpen] = useState(false);
  const [htmlCode, setHtmlCode] = useState('');
  
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }: { editor: Editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
      handlePaste: (view: any, event: ClipboardEvent) => {
        // Get pasted HTML from clipboard
        const html = event.clipboardData?.getData('text/html');
        if (html && html.trim()) {
          // If HTML is detected, parse and insert it
          event.preventDefault();
          
          // Parse HTML and insert at cursor position
          // Tiptap will automatically parse HTML when using setContent
          editor?.commands.insertContent(html);
          return true;
        }
        return false; // Let default paste handler handle plain text
      },
    },
  });

  const handlePasteHtml = () => {
    if (htmlCode.trim()) {
      try {
        // Set the HTML content directly
        editor?.commands.setContent(htmlCode);
        onChange(htmlCode);
        setPasteHtmlDialogOpen(false);
        setHtmlCode('');
        toast.success('HTML code inserted successfully');
      } catch (error: any) {
        toast.error('Failed to parse HTML: ' + (error.message || 'Invalid HTML'));
      }
    } else {
      toast.error('Please enter HTML code');
    }
  };

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        overflow: 'hidden',
        '& .tiptap': {
          minHeight: '200px',
          padding: '12px',
          fontSize: '14px',
          outline: 'none',
          '& p.is-editor-empty:first-child::before': {
            content: `"${placeholder ?? 'Start typing...'}"`,
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontWeight: 'bold',
            marginTop: '0.5em',
            marginBottom: '0.5em',
          },
          '& h1': { fontSize: '2em' },
          '& h2': { fontSize: '1.5em' },
          '& h3': { fontSize: '1.25em' },
          '& ul, & ol': {
            paddingLeft: '1.5em',
          },
          '& blockquote': {
            borderLeft: '4px solid #ddd',
            paddingLeft: '1em',
            marginLeft: 0,
            fontStyle: 'italic',
          },
          '& code': {
            backgroundColor: '#f4f4f4',
            padding: '2px 4px',
            borderRadius: '3px',
            fontFamily: 'monospace',
          },
          '& pre': {
            backgroundColor: '#f4f4f4',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          },
        },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          borderBottom: '1px solid #e0e0e0',
          padding: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px',
          backgroundColor: '#fafafa',
        }}
      >
        <Button
          size="small"
          variant={editor.isActive('bold') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <strong>B</strong>
        </Button>
        <Button
          size="small"
          variant={editor.isActive('italic') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <em>I</em>
        </Button>
        <Button
          size="small"
          variant={editor.isActive('strike') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          H1
        </Button>
        <Button
          size="small"
          variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          H2
        </Button>
        <Button
          size="small"
          variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          H3
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          •
        </Button>
        <Button
          size="small"
          variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          1.
        </Button>
        <Button
          size="small"
          variant={editor.isActive('blockquote') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          "
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => editor.chain().focus().undo().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          ↶
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => editor.chain().focus().redo().run()}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          ↷
        </Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Button
          size="small"
          variant="outlined"
          onClick={() => setPasteHtmlDialogOpen(true)}
          sx={{ minWidth: 'auto', px: 1 }}
          title="Paste HTML Code"
        >
          <CodeIcon fontSize="small" />
        </Button>
      </Box>
      <EditorContent editor={editor} />
      
      {/* Paste HTML Dialog */}
      <Dialog
        open={pasteHtmlDialogOpen}
        onClose={() => {
          setPasteHtmlDialogOpen(false);
          setHtmlCode('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Paste HTML Code</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Paste your HTML code below. It will be inserted into the editor and parsed as HTML.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={10}
              value={htmlCode}
              onChange={(e) => setHtmlCode(e.target.value)}
              placeholder="<p>Your HTML code here...</p>"
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '12px',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPasteHtmlDialogOpen(false);
            setHtmlCode('');
          }}>
            Cancel
          </Button>
          <Button onClick={handlePasteHtml} variant="contained">
            Insert HTML
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ContentManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<HtmlContent | null>(null);
  const [formData, setFormData] = useState<HtmlContentFormData>({
    type: 'contact_us',
    title: '',
    content: ''
  });
  const [titleValues, setTitleValues] = useState<Record<string, string>>({});
  const [contentValues, setContentValues] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const queryClient = useQueryClient();

  // Fetch languages
  const { data: languages, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await apiService.getLanguages();
      return response.data as Language[];
    }
  });

  // Fetch all HTML content
  const { data: contentData, isLoading, error, refetch } = useQuery({
    queryKey: ['htmlContent'],
    queryFn: () => apiService.getAllHtmlContent(),
    refetchOnWindowFocus: false
  });

  // Initialize default content mutation
  const initializeMutation = useMutation({
    mutationFn: () => apiService.initializeDefaultHtmlContent(),
    onSuccess: () => {
      toast.success('Default content initialized successfully');
      queryClient.invalidateQueries({ queryKey: ['htmlContent'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initialize default content');
    }
  });

  // Upsert content mutation
  const upsertMutation = useMutation({
    mutationFn: (data: HtmlContentFormData) => apiService.upsertHtmlContent(data),
    onSuccess: () => {
      toast.success(isEditing ? 'Content updated successfully' : 'Content created successfully');
      setEditDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['htmlContent'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save content');
    }
  });

  // Toggle status mutation
  const toggleMutation = useMutation({
    mutationFn: ({ type, isActive }: { type: string; isActive: boolean }) => 
      apiService.toggleHtmlContentStatus(type as any, isActive),
    onSuccess: () => {
      toast.success('Content status updated successfully');
      queryClient.invalidateQueries({ queryKey: ['htmlContent'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update content status');
    }
  });

  const contentTypes = [
    { value: 'contact_us', label: 'Contact Us', icon: '📞' },
    { value: 'privacy_policy', label: 'Privacy Policy', icon: '🔒' },
    { value: 'terms_conditions', label: 'Terms & Conditions', icon: '📋' }
  ];

  const getContentByType = (type: string) => {
    return contentData?.data?.find((content: HtmlContent) => content.type === type);
  };

  useEffect(() => {
    if (selectedContent && editDialogOpen) {
      // Handle multilingual title and content
      // Check if title is a string (legacy) or object (multilingual)
      const titleObj = typeof selectedContent.title === 'string' 
        ? { EN: selectedContent.title } 
        : (selectedContent.title || {});
      
      const contentObj = typeof selectedContent.content === 'string' 
        ? { EN: selectedContent.content || '' } 
        : (selectedContent.content || {});

      // Initialize title and content values for all active languages
      const activeLanguages = languages?.filter(l => l.isActive) || [];
      const initialTitleValues: Record<string, string> = {};
      const initialContentValues: Record<string, string> = {};
      
      activeLanguages.forEach(lang => {
        initialTitleValues[lang.code] = titleObj[lang.code] || titleObj[lang.code.toUpperCase()] || '';
        initialContentValues[lang.code] = contentObj[lang.code] || contentObj[lang.code.toUpperCase()] || '';
      });
      
      setTitleValues(initialTitleValues);
      setContentValues(initialContentValues);
    } else if (!selectedContent && editDialogOpen) {
      // Initialize empty values for all active languages
      const activeLanguages = languages?.filter(l => l.isActive) || [];
      const initialTitleValues: Record<string, string> = {};
      const initialContentValues: Record<string, string> = {};
      
      activeLanguages.forEach(lang => {
        initialTitleValues[lang.code] = '';
        initialContentValues[lang.code] = '';
      });
      
      setTitleValues(initialTitleValues);
      setContentValues(initialContentValues);
    }
  }, [selectedContent, editDialogOpen, languages]);

  const handleEdit = (content: HtmlContent) => {
    setSelectedContent(content);
    setFormData({
      type: content.type,
      title: '', // Not used anymore, kept for backward compatibility
      content: '' // Not used anymore, kept for backward compatibility
    });
    setIsEditing(true);
    setEditDialogOpen(true);
  };

  const handleAdd = (type: string) => {
    setSelectedContent(null);
    setFormData({
      type: type as any,
      title: '',
      content: ''
    });
    setIsEditing(false);
    setEditDialogOpen(true);
  };

  const handleView = (content: HtmlContent) => {
    setSelectedContent(content);
    setViewDialogOpen(true);
  };

  const handleToggleStatus = (content: HtmlContent) => {
    toggleMutation.mutate({
      type: content.type,
      isActive: !content.isActive
    });
  };

  const handleTranslate = async (translateType: 'title' | 'content' | 'both' = 'both') => {
    console.log("handleTranslate");
    // Find English language from languages array
    const englishLang = languages?.find(l => 
      l.code.toUpperCase() === 'EN' || 
      l.code.toLowerCase() === 'en' ||
      l.name.toLowerCase() === 'english'
    );
    
    const englishLangCode = englishLang?.code || 'en';
    const englishLangCodeUpper = englishLangCode.toUpperCase();
    const englishLangCodeLower = englishLangCode.toLowerCase();
    
    // Get English content - check all possible formats
    const englishTitle = titleValues[englishLangCode] ?? 
                        titleValues[englishLangCodeUpper] ?? 
                        titleValues[englishLangCodeLower] ?? 
                        titleValues['EN'] ?? 
                        titleValues['en'] ?? '';
    
    const englishContent = contentValues[englishLangCode] ?? 
                          contentValues[englishLangCodeUpper] ?? 
                          contentValues[englishLangCodeLower] ?? 
                          contentValues['EN'] ?? 
                          contentValues['en'] ?? '';

    console.log('English language code:', englishLangCode);
    console.log('Title values keys:', Object.keys(titleValues));
    console.log('Content values keys:', Object.keys(contentValues));
    console.log('English title found:', englishTitle ? `${englishTitle.substring(0, 50)}...` : 'NOT FOUND');
    console.log('English content found:', englishContent ? `${englishContent.substring(0, 50)}...` : 'NOT FOUND');
    console.log('English content length:', englishContent.length);
    console.log('English content trimmed length:', englishContent.trim().length);

    if (translateType === 'title' && !englishTitle) {
      toast.error('Please provide English title to translate');
      return;
    }

    if (translateType === 'content' && !englishContent) {
      toast.error('Please provide English content to translate');
      return;
    }

    if (translateType === 'both' && !englishTitle && !englishContent) {
      toast.error('Please provide English title or content to translate');
      return;
    }

    setIsTranslating(true);

    try {
      const activeLanguages = languages?.filter(l => {
        const code = l.code.toUpperCase();
        return l.isActive && code !== 'EN' && code !== englishLangCodeUpper;
      }) || [];
      
      if (activeLanguages.length === 0) {
        toast.error('No active languages found to translate to');
        return;
      }
      
      console.log(`Starting translation: type=${translateType}, languages=${activeLanguages.length}`);
      console.log('English title:', englishTitle ? `${englishTitle.substring(0, 50)}...` : 'empty');
      console.log('English content:', englishContent ? `${englishContent.substring(0, 50)}...` : 'empty');
      
      // Initialize final values with current state
      let finalTitleValues = { ...titleValues };
      let finalContentValues = { ...contentValues };
      
      let translatedTitleCount = 0;
      let translatedContentCount = 0;
      
      // Translate to all active languages except English
      for (const lang of activeLanguages) {
        const langCode = lang.code.toUpperCase();
        const langCodeLower = lang.code.toLowerCase();

        // Translate title if needed
        if (translateType === 'title' || translateType === 'both') {
          const currentTitle = finalTitleValues[langCode] || finalTitleValues[langCodeLower] || '';
          if (englishTitle && (!currentTitle || currentTitle.trim() === '')) {
            try {
              console.log(`[API CALL] Translating title to ${langCode}...`);
              const titleResponse = await apiService.translateText(englishTitle, langCode, 'en');
              console.log(`[API RESPONSE] Title translation for ${langCode}:`, titleResponse);
              
              if (titleResponse.success && titleResponse.data?.translatedText) {
                finalTitleValues[langCode] = titleResponse.data.translatedText;
                translatedTitleCount++;
                console.log(`✓ Title translated to ${langCode}:`, titleResponse.data.translatedText);
              } else {
                console.warn(`✗ Title translation failed for ${langCode}:`, titleResponse);
                toast.error(`Failed to translate title to ${lang.name}`);
              }
            } catch (error: any) {
              console.error(`✗ Exception translating title to ${langCode}:`, error);
              toast.error(`Failed to translate title to ${lang.name}: ${error.message || 'Unknown error'}`);
            }
          } else {
            console.log(`Skipping title translation for ${langCode} (already has value or no English title)`);
          }
        }

        // Translate content if needed
        if (translateType === 'content' || translateType === 'both') {
          const currentContent = finalContentValues[langCode] || finalContentValues[langCodeLower] || '';

          console.log("englishContent");
          console.log(englishContent);
          console.log("currentContent");
          console.log(currentContent);
          if (englishContent && (!currentContent || currentContent.trim() === '<p></p>')) {
            try {
              if (englishContent.trim()) {
                console.log(`[API CALL] Translating content to ${langCode}...`);
                const contentResponse = await apiService.translateHtml(englishContent, langCode, 'en');
                console.log(`[API RESPONSE] Content translation for ${langCode}:`, {
                  success: contentResponse.success,
                  hasData: !!contentResponse.data,
                  htmlLength: contentResponse.data?.translatedHtml?.length || 0
                });
                
                if (contentResponse.success && contentResponse.data?.translatedHtml) {
                  finalContentValues[langCode] = contentResponse.data.translatedHtml;
                  translatedContentCount++;
                  console.log(`✓ Content translated to ${langCode}, length:`, contentResponse.data.translatedHtml.length);
                } else {
                  console.warn(`✗ Content translation failed for ${langCode}:`, contentResponse);
                  toast.error(`Failed to translate content to ${lang.name}`);
                }
              }
            } catch (error: any) {
              console.error(`✗ Exception translating content to ${langCode}:`, error);
              console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
              });
              toast.error(`Failed to translate content to ${lang.name}: ${error.message || 'Unknown error'}`);
            }
          } else {
            console.log(`Skipping content translation for ${langCode} (already has value or no English content)`);
          }
        }
      }

      console.log(`Translation complete: ${translatedTitleCount} titles, ${translatedContentCount} content items translated`);

      setTitleValues(finalTitleValues);
      setContentValues(finalContentValues);
      
      // Only show success if translations actually happened
      if (translatedTitleCount > 0 || translatedContentCount > 0) {
        const parts = [];
        if (translatedTitleCount > 0) parts.push(`${translatedTitleCount} title(s)`);
        if (translatedContentCount > 0) parts.push(`${translatedContentCount} content item(s)`);
        toast.success(`Translated ${parts.join(' and ')} successfully`);
      } else {
        toast.error('No translations were made. All languages may already have content, or English content is missing.');
      }
    } catch (error: any) {
      console.error('Translation error:', error);
      toast.error(error.response?.data?.message || 'Failed to translate content. Please check if translation API is configured.');
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSubmit = () => {
    // Validate that at least one language has a title and content
    const hasTitle = Object.values(titleValues).some(val => val?.trim());
    const hasContent = Object.values(contentValues).some(val => val?.trim());
    
    if (!hasTitle) {
      toast.error('Title is required in at least one language');
      return;
    }
    
    if (!hasContent) {
      toast.error('Content is required in at least one language');
      return;
    }

    // Build multilingual title and content objects
    const titleObj: Record<string, string> = {};
    const contentObj: Record<string, string> = {};
    
    Object.keys(titleValues).forEach(code => {
      const value = titleValues[code]?.trim();
      if (value) {
        titleObj[code.toUpperCase()] = value;
      }
    });
    
    Object.keys(contentValues).forEach(code => {
      const value = contentValues[code]?.trim();
      if (value) {
        contentObj[code.toUpperCase()] = value;
      }
    });

    // Prepare form data with multilingual format
    const submitData = {
      ...formData,
      title: titleObj,
      content: contentObj,
    };

    upsertMutation.mutate(submitData);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load content. Please try again.
        </Alert>
        <Button onClick={() => refetch()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Content Management
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
         
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="content management tabs"
          variant="fullWidth"
        >
          {contentTypes.map((type, index) => (
            <Tab
              key={type.value}
              label={`${type.icon} ${type.label}`}
              id={`content-tab-${index}`}
              aria-controls={`content-tabpanel-${index}`}
            />
          ))}
        </Tabs>

        {contentTypes.map((type, index) => (
          <TabPanel key={type.value} value={tabValue} index={index}>
            <ContentTypePanel
              type={type}
              content={getContentByType(type.value)}
              onEdit={handleEdit}
              onAdd={handleAdd}
              onView={handleView}
              onToggleStatus={handleToggleStatus}
              isToggling={toggleMutation.isPending}
            />
          </TabPanel>
        ))}
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Edit Content' : 'Add Content'}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                disabled={isEditing}
              >
                {contentTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Multilingual Title Fields */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Title *
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={() => handleTranslate('title')}
                disabled={isTranslating || (!titleValues['EN'] && !titleValues['en'])}
                sx={{ ml: 2 }}
              >
                {isTranslating ? 'Translating...' : 'Translate Titles'}
              </Button>
            </Box>
            {languagesLoading ? (
              <CircularProgress size={20} />
            ) : (
              languages?.filter(l => l.isActive).map((lang) => (
                <TextField
                  key={lang.code}
                  fullWidth
                  label={`Title (${lang.name} - ${lang.nativeName})`}
                  value={titleValues[lang.code] || ''}
                  onChange={(e) => {
                    setTitleValues({
                      ...titleValues,
                      [lang.code]: e.target.value
                    });
                  }}
                  required={lang.code === 'EN' || lang.code === 'en'} // English is required
                  sx={{ mb: 2 }}
                  helperText={lang.code === 'EN' || lang.code === 'en' ? 'Required' : 'Optional'}
                />
              ))
            )}

            <Box sx={{ my: 2 }}>
              <Divider />
            </Box>

            {/* Multilingual Content Fields */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Content (HTML) *
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TranslateIcon />}
                onClick={() => handleTranslate('content')}
                disabled={isTranslating || (!contentValues['EN'] && !contentValues['en'])}
                sx={{ ml: 2 }}
              >
                {isTranslating ? 'Translating...' : 'Translate Content'}
              </Button>
            </Box>
            {languagesLoading ? (
              <CircularProgress size={20} />
            ) : (
              languages?.filter(l => l.isActive).map((lang) => (
                <Box key={lang.code} sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {lang.name} - {lang.nativeName} {lang.code === 'EN' || lang.code === 'en' ? '(Required)' : '(Optional)'}
                  </Typography>
                  <RichTextEditor
                    value={contentValues[lang.code] || ''}
                    onChange={(value) => {
                      setContentValues({
                        ...contentValues,
                        [lang.code]: value
                      });
                    }}
                    placeholder="Enter HTML content here..."
                  />
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={upsertMutation.isPending}
          >
            {upsertMutation.isPending ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {getDisplayTitle(selectedContent?.title)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last updated: {selectedContent?.lastUpdatedBy?.name} on{' '}
              {new Date(selectedContent?.updatedAt ?? '').toLocaleString()}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box
              dangerouslySetInnerHTML={{ __html: getDisplayContent(selectedContent?.content) ?? '' }}
              sx={{
                '& h1, & h2, & h3': { mt: 2, mb: 1 },
                '& p': { mb: 1 },
                '& ul, & ol': { pl: 2 }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface ContentTypePanelProps {
  type: { value: string; label: string; icon: string };
  content?: HtmlContent;
  onEdit: (content: HtmlContent) => void;
  onAdd: (type: string) => void;
  onView: (content: HtmlContent) => void;
  onToggleStatus: (content: HtmlContent) => void;
  isToggling: boolean;
}

const ContentTypePanel: React.FC<ContentTypePanelProps> = ({
  type,
  content,
  onEdit,
  onAdd,
  onView,
  onToggleStatus,
  isToggling
}) => {
  if (!content) {
    return (
      <Card>
        <CardContent>
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {type.label} content found
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create content for {type.label} to display in the mobile app.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => onAdd(type.value)}
            >
              Create {type.label}
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {type.icon} {getDisplayTitle(content.title)}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <Chip
                label={content.isActive ? 'Active' : 'Inactive'}
                color={content.isActive ? 'success' : 'default'}
                size="small"
              />
              <Chip
                label={`Version ${content.version}`}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={() => onView(content)}
              color="primary"
              title="View Content"
            >
              <ViewIcon />
            </IconButton>
            <IconButton
              onClick={() => onEdit(content)}
              color="primary"
              title="Edit Content"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => onToggleStatus(content)}
              color={content.isActive ? 'warning' : 'success'}
              title={content.isActive ? 'Deactivate' : 'Activate'}
              disabled={isToggling}
            >
              {content.isActive ? <ToggleOffIcon /> : <ToggleOnIcon />}
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          Last updated by <strong>{content.lastUpdatedBy?.name}</strong> on{' '}
          {new Date(content.updatedAt).toLocaleString()}
        </Typography>

        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            backgroundColor: '#fafafa'
          }}
        >
          <Box
            dangerouslySetInnerHTML={{ __html: getDisplayContent(content.content) }}
            sx={{
              '& h1, & h2, & h3': { mt: 1, mb: 0.5, fontSize: '0.9rem' },
              '& p': { mb: 0.5, fontSize: '0.8rem' },
              '& ul, & ol': { pl: 2, fontSize: '0.8rem' }
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ContentManagement;
